/**
 * This file contains the components used in the AccountInfo component
 * These components are the editing components for each field
 */

import React, { useState, useEffect, useRef } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { Button, Dropdown, Input, Typography } from '@evocolabs/orkaui'

import styles from '/src/components/scss/Account/AccountInfo.module.scss'
import { useSelector } from 'react-redux'
import { useBackend } from '@/src/providers/BackendProvider'
import { selector } from '@/src/redux/store'

/**
 * Base component for each edit/display item
 * @param {String} title
 * @param {String} content
 * @param {Boolean} disableEditing if true, an edit button will not occur
 * @param {String} activeFormTitle id of the form field being editing, null for no editing field
 * @param {function} setActiveFormTitle
 * @param {React.Component} editingComponent the sub React component which edits this field
 * @param {String} id to compose button and input id
 * @param {Object} formData the data for this field, parent's React state
 * @param {function} setFormData
 * @returns
 */
const ItemDisplay = ({
  title,
  content,
  disableEditing,
  activeFormTitle,
  setActiveFormTitle,
  editingComponent,
  id
}) => {
  const onCancel = () => setActiveFormTitle(null)
  const onEdit = () => {
    if (activeFormTitle === null) {
      setActiveFormTitle(title)
    }
  }
  return (
    <Box className={styles.cardContainer}>
      <Box className={styles.splitAlign}>
        <Typography variant="Body2">{title}</Typography>
        {!disableEditing &&
          (title !== activeFormTitle ? (
            <Button
              variant="textprimary"
              leftIcon
              onClick={onEdit}
              id={`account-details-page-${id}-section-edit-button`}
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="textprimary"
              onClick={onCancel}
              id={`account-details-page-${id}-section-cancel-button`}
            >
              Cancel
            </Button>
          ))}
      </Box>
      <Box>
        {title !== activeFormTitle ? (
          <Typography variant="body2">{content ? content : '/'}</Typography>
        ) : (
          editingComponent
        )}
      </Box>
    </Box>
  )
}

const NameField = (props) => {
  const { setActiveFormTitle, firstName, lastName } = props
  const theme = useTheme()
  const useMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { patchUser } = useBackend()
  const EditingComponent = () => {
    const [formData, setFormData] = useState({
      first_name: firstName,
      last_name: lastName
    })
    const [errormsg, setErrormsg] = useState([null, null])
    const errormsgTmp = [null, null]

    const onSave = async () => {
      setErrormsg([null, null])
      if (!formData.first_name) {
        errormsgTmp[0] = 'This field is required'
      }
      if (!formData.last_name) {
        errormsgTmp[1] = 'This field is required'
      }
      if (formData.first_name && formData.last_name) {
        await patchUser(formData)
        setActiveFormTitle(null)
      } else {
        setErrormsg(errormsgTmp)
      }
    }

    return (
      <Box className={styles.cardDropContainer}>
        <Input
          placeholder="First Name"
          value={formData.first_name}
          fullWidth
          inputProps={{ maxLength: 50 }}
          onChange={(e) =>
            setFormData({ ...formData, first_name: e.target.value })
          }
          errormsg={errormsg[0]}
          id="account-details-page-first-name-input"
        />
        <Input
          placeholder="Last Name"
          value={formData.last_name}
          inputProps={{ maxLength: 50 }}
          fullWidth
          onChange={(e) =>
            setFormData({ ...formData, last_name: e.target.value })
          }
          errormsg={errormsg[1]}
          id="account-details-page-last-name-input"
        />
        <Box className={styles.rightAlign}>
          <Button
            block={useMobile}
            size="large"
            onClick={onSave}
            id="account-details-page-name-section-save-button"
          >
            Save
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <ItemDisplay
      title="Name"
      editingComponent={<EditingComponent />}
      id="name"
      {...props}
    />
  )
}

const DropdownField = (props) => {
  const { id, content, setActiveFormTitle, choices, fieldName } = props
  const theme = useTheme()
  const useMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { patchUser } = useBackend()
  const EditingComponent = () => {
    const [formData, setFormData] = useState(content)
    const [errormsg, setErrormsg] = useState('')

    const onSave = async () => {
      if (!formData) {
        setErrormsg('This field is required')
      } else {
        await patchUser({ [fieldName]: formData })
        setActiveFormTitle(null)
      }
    }

    return (
      <Box className={styles.cardDropContainer}>
        <Dropdown
          value={formData}
          onChange={(e) => setFormData(e.target.value)}
          errormsg={errormsg}
          choice={choices}
        />
        <Box className={styles.rightAlign}>
          <Button
            block={useMobile}
            size="large"
            onClick={onSave}
            id={`account-details-page-${id}-save-button`}
          >
            Save
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <ItemDisplay
      title="Name"
      editingComponent={<EditingComponent />}
      {...props}
    />
  )
}

const HearingConditionDropdownField = (props) => {
  const { setActiveFormTitle, choices } = props
  const theme = useTheme()
  const useMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [currentHearingCondition, setCurrentHearingCondition] =
    useState('Loading')
  const [errormsg, setErrormsg] = useState('')

  const kycIdRef = useRef(null)
  const kycQuestion = useRef(null)
  const { getLatestHearingCondition, patchKYCResult } = useBackend()
  const loggedUser = useSelector(selector.selectUserInfo).uuid
  const getKYCResults = async () => {
    const results = await getLatestHearingCondition(loggedUser)
    const { hearingCondition, kycId, kycQuestionData } = results
    kycIdRef.current = kycId
    kycQuestion.current = kycQuestionData
    setCurrentHearingCondition(hearingCondition)
  }
  useEffect(() => {
    getKYCResults()
  }, [])

  const EditingComponent = () => {
    if (kycIdRef.current === null)
      return (
        <Box className={styles.cardContainer}>
          <Typography variant="body2">
            Complete our survey to record your hearing condition.
          </Typography>
          <Box className={styles.rightAlign}>
            <Button
              variant="primary"
              onClick={() => (window.location.href = '/SurveyPage')}
              id="account-details-page-start-survey-button"
            >
              Start Survey
            </Button>
          </Box>
        </Box>
      )

    const onSave = async () => {
      if (!currentHearingCondition) {
        setErrormsg('This field is required')
      } else {
        await patchKYCResult(kycIdRef.current, {
          survey_result_items: [
            {
              ...kycQuestion.current,
              answer: currentHearingCondition
            }
          ]
        })
        setActiveFormTitle(null)
      }
    }

    return (
      <Box className={styles.cardContainer}>
        <Dropdown
          value={currentHearingCondition}
          onChange={(e) => setCurrentHearingCondition(e.target.value)}
          errormsg={errormsg}
          choice={choices}
        />
        <Box className={styles.rightAlign}>
          <Button
            block={useMobile}
            size="large"
            handleClick={onSave}
            loadable
            id="account-info-hearing-condition-save"
          >
            Save
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <ItemDisplay
      title="Name"
      editingComponent={<EditingComponent />}
      content={currentHearingCondition}
      disableEditing={currentHearingCondition === 'Loading...'}
      {...props}
    />
  )
}

const PasswordField = (props) => {
  const { setActiveFormTitle, email } = props
  const theme = useTheme()
  const useMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { userReAuth, userUpdatePassword } = useBackend()
  const EditingComponent = () => {
    const [formData, setFormData] = useState({
      password: '',
      newPassword: '',
      confirmNewPassword: ''
    })
    const [errors, setErrors] = useState([null, null, null, null])

    const onSave = async () => {
      let error = false
      setErrors([null, null, null, null])
      const errorsTmp = [null, null, null, null]
      if (!formData.password) {
        errorsTmp[0] = 'This field is required'
        error = true
      }
      if (!formData.newPassword) {
        errorsTmp[1] = 'This field is required'
        error = true
      } else if (formData.newPassword.length < 6) {
        errorsTmp[1] = 'Password must be at least 6 characters'
        error = true
      }
      if (!formData.confirmNewPassword) {
        errorsTmp[2] = 'This field is required'
        error = true
      }
      if (formData.newPassword !== formData.confirmNewPassword) {
        errorsTmp[2] = 'New password does not match'
        error = true
      }
      if (!error) {
        const res = await userReAuth(email, formData.password)
        if (res !== 'success') {
          errorsTmp[0] = 'Incorrect password'
          error = true
        } else {
          const updateCompleted = await userUpdatePassword({
            username: window.localStorage.getItem('userID'),
            password: formData.newPassword
          })
          if (updateCompleted) {
            setActiveFormTitle(null)
          } else {
            errorsTmp[3] = 'Failed to set password, please try again later.'
            error = true
          }
        }
      }
      if (error) {
        setErrors(errorsTmp)
      }
    }

    return (
      <Box className={styles.cardDropContainer}>
        <Input
          placeholder="Current Password"
          value={formData.password}
          fullWidth
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
          type="password"
          errormsg={errors[0]}
          id="account-details-page-password-section-current-password"
        />
        <Input
          placeholder="New Password"
          value={formData.newPassword}
          fullWidth
          onChange={(e) =>
            setFormData({ ...formData, newPassword: e.target.value })
          }
          type="password"
          errormsg={errors[1]}
          id="account-details-page-password-section-new-password"
        />
        <Input
          placeholder="Confirm Password"
          value={formData.confirmNewPassword}
          fullWidth
          onChange={(e) =>
            setFormData({ ...formData, confirmNewPassword: e.target.value })
          }
          type="password"
          errormsg={errors[2]}
          id="account-details-page-password-section-confirm-password"
        />
        {errors[3] ? (
          <Typography color={theme.palette.error[300]} variant="body4">
            {errors[3]}
          </Typography>
        ) : null}
        <Box className={styles.rightAlign}>
          <Button
            block={useMobile}
            size="large"
            handleClick={onSave}
            loadable
            id="account-details-page-password-section-save-button"
          >
            Save
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <ItemDisplay
      title="Name"
      content="************"
      editingComponent={<EditingComponent />}
      {...props}
    />
  )
}

export {
  ItemDisplay,
  NameField,
  DropdownField,
  PasswordField,
  HearingConditionDropdownField
}
